import { isEmpty } from 'ramda'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useDialogController } from '@machobear/design-system'
import { Avatar } from '@/components/avatar'
import Icon from '@/components/icon'
import VerifiedBadge from '@/components/verified-badge'
import useGetListingInquiries from '@/hooks/use-get-listing-inquiries'
import { colors, breakpoints, fontSizes, fontWeights } from '@/styles'
import formatName from '@/utils/format-name'
import { getImageUrl } from '@/utils/image'
import UserPicker from '@/components/user-picker/'

const TenantSelectWrapper = styled.div`
  height: 65px;
  width: 100%;
  max-width: 325px;
  border-radius: 6px;
  border: 1px solid ${colors.grey};
`

const TenantSelectContent = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  width: 100%;
  padding: 20px;
  background: none;
  border: none;
  ${props =>
    props.clickable &&
    `
    cursor: pointer;
  `}

  ${props =>
    props.$disabled &&
    `
    opacity: 0.5;
    cursor: not-allowed;
  `};
`

const LandlordInfo = styled.div`
  display: flex;
  align-items: center;
`

const SelectorText = styled.p`
  color: ${colors.regular};
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.medium};
  margin: 0;
`

const NameText = styled(SelectorText)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 142px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    max-width: 130px;
  }

  @media screen and (max-width: ${breakpoints.smallPhone}) {
    max-width: 90px;
  }
`

const RemoveText = styled.button`
  color: ${colors.link};
  font-size: ${fontSizes.small};
  background-color: ${colors.white};
  border: none;
  cursor: pointer;
  background: none;
  padding: 0;

  :active {
    opacity: 0.5;
  }
`

const Tenant = ({ user, handleRemove }) => {
  const { t } = useTranslation()

  return (
    <TenantSelectContent>
      <LandlordInfo>
        <Avatar
          location={getImageUrl(user.avatar?.aws_s3_key, {
            width: 80,
            height: 80
          })}
          absolute={false}
          shadow
          width={40}
          height={40}
          right={15}
        />
        <NameText>{formatName(user)}</NameText>
        <VerifiedBadge
          type="id"
          width={24}
          height={18}
          right={5}
          left={5}
          verified={user.identity_verified_state === 'verified'}
        />
      </LandlordInfo>
      <RemoveText onClick={handleRemove} type="button">
        {t('common.remove', 'Remove')}
      </RemoveText>
    </TenantSelectContent>
  )
}

const TenantSelector = ({ values, setFieldValue, listingId }) => {
  const { t } = useTranslation()
  const { isLoading, data } = useGetListingInquiries(listingId)
  const { visible, showModal, closeModal } = useDialogController()

  const hasSelection = !!values.tenant.id

  const handleSetTenant = user => setFieldValue('tenant', user[0])
  const handleRemoveTenant = () => setFieldValue('tenant', {})

  const handleTenantClick = () => {
    hasSelection ? handleRemoveTenant() : showModal()
  }

  if (isLoading)
    return (
      <TenantSelectWrapper>
        <TenantSelectContent $disabled>
          <SelectorText>{t('l.mark_rented.edit.add_a_tenant.button', 'Add a tenant')}</SelectorText>
          <Icon id="plus" height={18} width={18} />
        </TenantSelectContent>
      </TenantSelectWrapper>
    )

  if (isEmpty(data))
    return (
      <TenantSelectWrapper>
        <TenantSelectContent>
          <SelectorText>
            {t('l.mark_rented.edit.no_tenants_available.button', 'No tenants available')}
          </SelectorText>
        </TenantSelectContent>
      </TenantSelectWrapper>
    )

  return (
    <>
      <TenantSelectWrapper>
        {hasSelection ? (
          <Tenant user={values.tenant} handleRemove={handleTenantClick} />
        ) : (
          <TenantSelectContent onClick={handleTenantClick} clickable as="button" type="button">
            <SelectorText>
              {t('l.mark_rented.edit.add_a_tenant.button', 'Add a tenant')}
            </SelectorText>
            <Icon id="plus" height={18} width={18} />
          </TenantSelectContent>
        )}
      </TenantSelectWrapper>
      <UserPicker
        visible={visible}
        onClose={closeModal}
        title={t(t('l.listing.edit.select_tenant.title', 'Select Tenant'))}
        maxSelect={1}
        allUsers={data}
        activeUsers={[]}
        setActiveUsers={handleSetTenant}
      />
    </>
  )
}

export default TenantSelector
